// Display formatting for Vendor-shaped extracted field values, shared by the
// turn-1 confirm list and the turn-2 enrichment review so both present
// structured fields (address / socialMediaUrls / additionalContacts) the same
// way — never String(object).
import type { Address, Contact } from '@stash/shared-types';

/** "1 Main St, Suite 4" / "Chicago, IL 60602" — mirrors composeAddress conventions. */
function addressLines(address: Partial<Address>): string[] {
  const street = [address.street, address.street2].filter(Boolean).join(', ');
  const locality = [
    address.city,
    [address.region, address.postalCode].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  return [street, locality].filter((line) => line.length > 0);
}

function contactLine(contact: Contact): string {
  if ('phone' in contact) {
    return `📞 ${contact.phone.number}${contact.phone.label ? ` · ${contact.phone.label}` : ''}`;
  }
  return `✉️ ${contact.email.email}${contact.email.label ? ` · ${contact.email.label}` : ''}`;
}

/** Multi-line display value for the field body. */
export function valueLines(field: string, value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (field === 'address' && typeof value === 'object' && !Array.isArray(value)) {
    return addressLines(value as Partial<Address>);
  }
  if (field === 'socialMediaUrls' && Array.isArray(value)) {
    return value.map((url) => String(url));
  }
  if (field === 'additionalContacts' && Array.isArray(value)) {
    return (value as Contact[]).map(contactLine);
  }
  if (typeof value === 'object') {
    // Unknown structured shape — readable JSON beats [object Object].
    return [JSON.stringify(value)];
  }
  return [String(value)];
}

/** Compact single-line preview for alternative-toggle buttons. */
export function previewValue(field: string, value: unknown): string {
  return valueLines(field, value).join(' · ');
}
